import { useState } from 'react';
import {
  productUrlForDisplay,
  withShopsMtOutboundUrl,
} from '../outboundUrl';
import { trackClick } from '../analytics';
import {
  resolveGaSourcePage,
  trackOpenSignupModal,
  trackOutboundClick,
  trackReportProductOpen,
} from '../ga';
import { FavoriteSignupModal } from './FavoriteSignupModal';
import { ReportModal } from './ReportModal';

const eurPriceFormatter = new Intl.NumberFormat('mt-MT', {
  style: 'currency',
  currency: 'EUR',
});

const eurWholeFormatter = new Intl.NumberFormat('mt-MT', {
  maximumFractionDigits: 0,
});

function splitPriceAmount(amount) {
  const roundedCents = Math.round(Number(amount) * 100);
  const whole = Math.trunc(roundedCents / 100);
  const cents = String(Math.abs(roundedCents % 100)).padStart(2, '0');

  return {
    whole: eurWholeFormatter.format(whole),
    cents,
  };
}

function isZeroPrice(amount) {
  return amount != null && Number(amount) === 0;
}

function formatPriceAriaLabel(amount) {
  if (amount == null || Number.isNaN(Number(amount))) {
    return 'Price unavailable';
  }
  return eurPriceFormatter.format(Number(amount));
}

/**
 * When priceNow is 0, show compare-at as the price or "?" if none.
 * @returns {{ displayNow: number|null, displayWas: number|null, priceUnknown: boolean }}
 */
function resolveProductPrices(priceNow, priceWas) {
  if (isZeroPrice(priceNow)) {
    if (priceWas != null && !isZeroPrice(priceWas)) {
      return { displayNow: priceWas, displayWas: null, priceUnknown: false };
    }
    return { displayNow: null, displayWas: null, priceUnknown: true };
  }

  if (priceNow == null) {
    if (priceWas != null && !isZeroPrice(priceWas)) {
      return { displayNow: priceWas, displayWas: null, priceUnknown: false };
    }
    return { displayNow: null, displayWas: null, priceUnknown: false };
  }

  const hasDiscount =
    priceWas != null &&
    !isZeroPrice(priceWas) &&
    Number(priceNow) < Number(priceWas);

  return {
    displayNow: priceNow,
    displayWas: hasDiscount ? priceWas : null,
    priceUnknown: false,
  };
}

function PriceAmount({ amount }) {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '—';
  }

  const { whole, cents } = splitPriceAmount(amount);

  return (
    <span className="product-card__price-amount">
      €{whole}
      <span className="product-card__price-cents">{cents}</span>
    </span>
  );
}

/** @returns {number|null} Rounded percent off when there is a real discount, else null. */
function discountPercentOff(priceWas, priceNow) {
  if (priceWas == null || priceNow == null) {
    return null;
  }
  const was = Number(priceWas);
  const now = Number(priceNow);
  if (!Number.isFinite(was) || !Number.isFinite(now) || was <= 0 || now >= was) {
    return null;
  }
  const pct = Math.round(((was - now) / was) * 100);
  return pct > 0 ? pct : null;
}

function BellIconSmall() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.73 21a2 2 0 0 1-3.46 0"
      />
    </svg>
  );
}

function FlagIconSmall() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7"
      />
    </svg>
  );
}

function productAlertSignupLabel(inStock) {
  const alertType = inStock === false ? 'stock status' : 'price';
  return `Get ${alertType} alerts for this product`;
}

function CardFavoriteButton({ disabled, inStock, onClick }) {
  const signupLabel = productAlertSignupLabel(inStock);

  return (
    <button
      type="button"
      className={
        disabled
          ? 'product-card__price-alert-btn product-card__price-alert-btn--disabled'
          : 'product-card__price-alert-btn'
      }
      aria-label={signupLabel}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
          onClick();
        }
      }}
    >
      <BellIconSmall />
      <span className="product-card__price-alert-tooltip">
        {signupLabel}
      </span>
    </button>
  );
}

function CardReportButton({ onClick }) {
  return (
    <button
      type="button"
      className="product-card__report-btn"
      aria-label="Report an issue with this product"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      <FlagIconSmall />
    </button>
  );
}

function availabilityLabel(inStock) {
  if (inStock === true) {
    return 'In stock';
  }
  if (inStock === false) {
    return 'Out of stock';
  }
  return 'Check availability';
}

function PricesBlock({
  displayNow,
  displayWas,
  priceUnknown,
}) {
  const hasDiscount = displayWas != null;

  return (
    <div className="product-card__prices" aria-label="Price">
      {priceUnknown && (
        <span className="product-card__price-single" aria-label="Price unknown">
          ?
        </span>
      )}
      {!priceUnknown && displayNow != null && (
        <span
          className={
            hasDiscount ? 'product-card__price-now' : 'product-card__price-single'
          }
          aria-label={formatPriceAriaLabel(displayNow)}
        >
          <PriceAmount amount={displayNow} />
        </span>
      )}
      {hasDiscount && (
        <span
          className="product-card__price-was"
          aria-label={formatPriceAriaLabel(displayWas)}
        >
          <PriceAmount amount={displayWas} />
        </span>
      )}
    </div>
  );
}

/**
 * @param {{
 *   product: object,
 *   layout?: 'grid' | 'list',
 *   outboundCampaign?: string,
 *   outboundContent?: string,
 *   analyticsContext?: {
 *     query?: string|null,
 *     shopCategorySlug?: string|null,
 *     source?: string,
 *     page?: number|null,
 *     position?: number|null,
 *     searchFallbackStep?: number|null,
 *   },
 * }} props
 */
export function ProductCard({
  product,
  layout = 'grid',
  outboundCampaign,
  outboundContent = 'product_card',
  analyticsContext,
}) {
  const [favoriteOpen, setFavoriteOpen] = useState(false);
  const [reportOpen,   setReportOpen]   = useState(false);

  const {
    id,
    title,
    url,
    image,
    currency,
    priceWas,
    priceNow,
    merchant,
    inStock,
    description,
    additionalImageCount = 0,
  } = product;

  const showImage =
    typeof image === 'string' && image.trim().length > 0;

  const extraImages =
    typeof additionalImageCount === 'number' &&
    additionalImageCount > 0
      ? additionalImageCount
      : 0;

  const totalPhotos = extraImages > 0 ? extraImages + 1 : 0;

  const mediaAriaLabel =
    totalPhotos > 1
      ? `View ${title} — ${totalPhotos} photos`
      : `View ${title}`;

  const { displayNow, displayWas, priceUnknown } = resolveProductPrices(
    priceNow,
    priceWas,
  );

  const hasDiscount =
    !isZeroPrice(priceNow) &&
    priceNow != null &&
    priceWas != null &&
    Number(priceNow) < Number(priceWas);

  const discountPct = hasDiscount
    ? discountPercentOff(priceWas, priceNow)
    : null;

  const availabilityClass =
    inStock === true
      ? 'product-card__stock-dot--in'
      : inStock === false
        ? 'product-card__stock-dot--out'
        : 'product-card__stock-dot--unknown';

  const descText =
    typeof description === 'string' && description.trim().length > 0
      ? description.trim()
      : null;

  const availText = availabilityLabel(inStock);

  function openAlertSignup() {
    trackOpenSignupModal('product_price_alert', 'product');
    setFavoriteOpen(true);
  }

  const productUrl = typeof url === 'string' ? url.trim() : '';
  const productUrlOut = withShopsMtOutboundUrl(productUrl, {
    content: outboundContent,
    campaign: outboundCampaign,
  });
  const hasProductLink = productUrlOut !== '';
  const merchantText =
    typeof merchant === 'string' ? merchant.trim() : '';

  const productId =
    typeof id === 'string'
      ? id.trim()
      : typeof id === 'number' && Number.isFinite(id)
        ? String(id)
        : '';

  const urlDisplay =
    productUrlOut !== ''
      ? productUrlForDisplay(productUrlOut)
      : productUrl !== ''
        ? productUrlForDisplay(productUrl)
        : '';

  function trackMysqlOutboundClick() {
    if (!analyticsContext || productId === '') {
      return;
    }
    trackClick({
      listingProductId: productId,
      query: analyticsContext.query ?? null,
      shopCategorySlug: analyticsContext.shopCategorySlug ?? null,
      source: analyticsContext.source ?? outboundCampaign,
      page: analyticsContext.page ?? null,
      position: analyticsContext.position ?? null,
      searchFallbackStep: analyticsContext.searchFallbackStep ?? null,
    });
  }

  function trackGaOutbound(clickTarget) {
    const sourceContext = analyticsContext?.source ?? outboundCampaign ?? 'browse';
    trackOutboundClick({
      clickTarget,
      sourcePage: resolveGaSourcePage(outboundCampaign, analyticsContext?.source),
      sourceContext,
      position: analyticsContext?.position ?? null,
    });
  }

  const visitStoreLink = hasProductLink ? (
    <a
      className="product-card__visit-store"
      href={productUrlOut}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={
        merchantText !== ''
          ? `Visit ${merchantText} for ${title}`
          : `Visit store for ${title}`
      }
      onClick={(e) => {
        e.stopPropagation();
        trackMysqlOutboundClick();
        trackGaOutbound('visit_store_button');
      }}
    >
      Visit store
    </a>
  ) : null;

  const sourceFooter =
    merchantText !== '' || urlDisplay !== '' ? (
      <div className="product-card__source-meta">
        {merchantText !== '' && (
          <p className="product-card__source-label">{merchantText}</p>
        )}
        {urlDisplay !== '' && (
          <p className="product-card__url-row">
            <span
              className="product-card__url"
              title={productUrlOut || productUrl}
            >
              {urlDisplay}
            </span>
          </p>
        )}
      </div>
    ) : null;

  const storeBlock =
    visitStoreLink || sourceFooter ? (
      <div className="product-card__store-block">
        {visitStoreLink}
        {sourceFooter}
      </div>
    ) : null;

  const articleClass = [
    'product-card',
    layout === 'list' ? 'product-card--layout-list' : null,
    !showImage ? 'product-card--no-image' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const pricesEl = (
    <PricesBlock
      displayNow={displayNow}
      displayWas={displayWas}
      priceUnknown={priceUnknown}
    />
  );

  const priceSlot = (
    <div className="product-card__price-slot">{pricesEl}</div>
  );

  const availabilityBlock = (
    <p
      className={
        layout === 'list'
          ? 'product-card__availability product-card__availability--list'
          : 'product-card__availability'
      }
      role="status"
      aria-label={`Availability: ${availText}`}
    >
      <span
        className={`product-card__stock-dot ${availabilityClass}`}
        aria-hidden="true"
      />
      <span className="product-card__availability-text">{availText}</span>
      {inStock === false && (
        <button
          type="button"
          className="product-card__stock-notify-btn"
          disabled={!hasProductLink}
          aria-label="Get stock status alerts for this product"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (hasProductLink) {
              openAlertSignup();
            }
          }}
        >
          Notify me
        </button>
      )}
    </p>
  );

  const titleRow = <p className="product-card__title">{title}</p>;

  const priceBlock = (
    <div className="product-card__price-block">
      <div className="product-card__price-row">{priceSlot}</div>
      {availabilityBlock}
    </div>
  );

  const footerBlock = storeBlock ? (
    <div className="product-card__footer">{storeBlock}</div>
  ) : null;

  const bodyContent = (
    <>
      <div className="product-card__content">
        {titleRow}
        {priceBlock}
        {descText != null && (
          <p className="product-card__desc" title={descText}>
            {descText}
          </p>
        )}
      </div>
      {footerBlock}
    </>
  );

  return (
    <article
      className={articleClass}
      {...(productId !== '' ? { 'data-product-id': productId } : {})}
    >
      <div
        className={
          showImage
            ? 'product-card__media'
            : 'product-card__media product-card__media--placeholder'
        }
        aria-hidden={hasProductLink ? 'true' : undefined}
      >
        <div
          className={
            showImage
              ? 'product-card__media-inner'
              : 'product-card__media-inner product-card__media-inner--placeholder'
          }
        >
          {extraImages > 0 && (
            <>
              <span className="sr-only">
                {totalPhotos} product photos — this card shows the first.
              </span>
              <span className="product-card__gallery-more" aria-hidden="true">
                +{extraImages} {extraImages === 1 ? 'image' : 'images'}
              </span>
            </>
          )}
          {discountPct != null && (
            <span
              className="product-card__discount-badge"
              aria-label={`${discountPct}% off`}
            >
              −{discountPct}%
            </span>
          )}
          {showImage ? (
            <img
              className="product-card__img"
              src={image}
              alt=""
              loading="lazy"
            />
          ) : (
            <span className="product-card__media-placeholder" aria-hidden="true" />
          )}
          <div className="product-card__report-action">
            <CardReportButton
              onClick={() => {
                trackReportProductOpen();
                setReportOpen(true);
              }}
            />
          </div>
        </div>
        <div className="product-card__corner-actions">
          <CardFavoriteButton
            disabled={!hasProductLink}
            inStock={inStock}
            onClick={openAlertSignup}
          />
        </div>
      </div>
      {layout === 'list' ? (
        <div className="product-card__body product-card__body--list">
          {bodyContent}
        </div>
      ) : (
        <div className="product-card__body">{bodyContent}</div>
      )}
      {hasProductLink && (
        <a
          className="product-card__stretch-link"
          href={productUrlOut}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={mediaAriaLabel}
          onMouseDown={() => {
            trackMysqlOutboundClick();
            trackGaOutbound('card');
          }}
        />
      )}
      {hasProductLink && favoriteOpen && (
        <FavoriteSignupModal
          onClose={() => setFavoriteOpen(false)}
          productUrl={productUrl}
          productTitle={typeof title === 'string' ? title : ''}
          productImage={image}
          priceNow={priceNow}
          priceWas={priceWas}
          merchant={merchant}
          inStock={inStock}
        />
      )}
      {reportOpen && (
        <ReportModal
          onClose={() => setReportOpen(false)}
          productUrl={productUrl}
          productTitle={typeof title === 'string' ? title : ''}
        />
      )}
    </article>
  );
}
